"use client";
import ICMWrapper from "@/app/components/icm-wrapper";
import React from "react";
import ReferVoucher from "./refer-voucher";
import Minus from "@/app/assets/icons/minus";
import { ButtonSize, ButtonVariant, IconButton } from "@/stories/icon-button/icon-button";
import Plus from "@/app/assets/icons/plus";
import { formatIndianCurrency, setRedirectTo } from "@/app/utils/helper";
import { CustomizableSlider } from "@/app/components/shared/Custom-Sldier";
import Info from "@/app/assets/icons/info";
import { RegularButton, ButtonSize as RegularButtonSize } from "@/stories/button/button";
import useMobile from "@/app/hooks/use-mobile";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
// import { initBranch, trackEvent } from "@/app/utils/branchio";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
import { useRouter } from "next/navigation";

interface ReferCalculatorProps {
    active?: string;
    noOfFriends: number;
    handleFriendsChange: (arg0: boolean) => void;
    amtValue: number;
    setAmtValue: (arg0: number) => void;
    calculateReturns: () => number;
    footerText?: string;
    authLoading?: boolean;
    voucherAmt?: number;
    voucherText?: string;
    hideTransactionBenefit?: boolean;
}

const ReferCalculator: React.FC<ReferCalculatorProps> = ({
    active,
    noOfFriends,
    handleFriendsChange,
    amtValue,
    setAmtValue,
    calculateReturns,
    footerText,
    authLoading,
    voucherAmt,
    voucherText,
    hideTransactionBenefit,
}) => {
    const { isMobile } = useMobile();
    const router = useRouter();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    if (authLoading) {
        return (
            <>
                <ICMWrapper>
                    <div className="flex flex-col items-center justify-center gap-s14 py-s9 md:flex-row md:justify-between md:gap-s17 md:py-s17">
                        <ReferVoucher isLoading={authLoading} voucherAmt={voucherAmt} voucherText={voucherText} hideTransactionBenefit={hideTransactionBenefit} />
                        <div className="hidden md:flex flex-1 animate-pulse flex-col items-center justify-center rounded-3xl bg-white shadow-lg">
                            <div className="w-full border-b px-s13 py-s9 text-center text-lg font-semibold leading-[23px] text-neutral-900">
                                <div className="mx-auto h-6 w-1/3 rounded bg-neutral-200" />
                            </div>
                            <div className="flex w-full flex-col flex-wrap sm:flex-row">
                                <div className="flex flex-1 flex-col gap-s11 px-s13 py-s8 md:py-s14">
                                    <div className="flex flex-col items-center justify-center gap-s4">
                                        <div className="h-4 w-2/3 rounded bg-neutral-200" />
                                        <div className="flex items-center justify-center gap-s8">
                                            <div className="h-10 w-10 rounded-full bg-neutral-200" />
                                            <div className="h-8 w-12 rounded bg-neutral-200" />
                                            <div className="h-10 w-10 rounded-full bg-neutral-200" />
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-center justify-center gap-s5">
                                        <div className="h-4 w-2/3 rounded bg-neutral-200" />
                                        <div className="h-8 w-24 rounded bg-neutral-200" />
                                        <div className="h-2 w-full rounded bg-neutral-200" />
                                    </div>
                                </div>
                                <div className="my-s8 flex flex-1 items-center justify-center sm:mr-s6">
                                    <div className="flex h-full min-w-[85%] max-w-[85%] items-center justify-center rounded-xl bg-neutral-50 py-s6 md:min-w-[90%] md:max-w-[90%] md:px-s13 md:py-s9">
                                        <div className="flex w-full flex-col items-center gap-s4 p-s9">
                                            <div className="h-4 w-1/3 rounded bg-neutral-200" />
                                            <div className="h-10 w-1/2 rounded bg-neutral-200" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ICMWrapper>
                <div className="flex w-full flex-col items-center justify-center gap-s9 px-s9">
                    <div className="h-12 w-full rounded bg-neutral-200" />
                    <div className="h-4 w-2/3 rounded bg-neutral-200" />
                </div>
            </>
        );
    }

    return (
        <>
            <ICMWrapper>
                <div className="flex flex-col items-center justify-center gap-s14 py-s9 md:flex-row md:justify-between md:gap-s17 md:py-s17">
                    <ReferVoucher voucherAmt={voucherAmt} voucherText={voucherText} hideTransactionBenefit={hideTransactionBenefit} />
                    <div
                        className={`flex flex-1 flex-col items-center justify-center rounded-3xl bg-white shadow-lg ${
                            active ? "hidden md:flex" : ""
                        }`}
                    >
                        {" "}
                        <div className="w-full border-b px-s13 py-s9 text-center text-lg font-semibold leading-[23px] text-neutral-900">
                            Calculate your earnings
                        </div>
                        <div className="flex w-full flex-col flex-wrap sm:flex-row">
                            <div className="flex flex-1 flex-col gap-s11 px-s13 py-s8 md:py-s14">
                                <div className="flex flex-col items-center justify-center gap-s4">
                                    <div className="text-sm leading-[19.6px] text-neutral-500">
                                        Total number of friends you refer
                                    </div>
                                    <div className="flex items-center justify-center gap-s8">
                                        <IconButton
                                            icon={<Minus />}
                                            size={ButtonSize.Extrasmall}
                                            disabled={noOfFriends === 0}
                                            onClick={() => handleFriendsChange(false)}
                                            variant={ButtonVariant.SecondaryDefault}
                                            className="bg-white"
                                            label="-"
                                        />
                                        <span className="tracking-[]-1.4px text-3xl font-bold leading-[24px] text-neutral-900">
                                            {noOfFriends}
                                        </span>
                                        <IconButton
                                            icon={<Plus />}
                                            onClick={() => handleFriendsChange(true)}
                                            size={ButtonSize.Extrasmall}
                                            variant={ButtonVariant.SecondaryDefault}
                                            className="bg-white"
                                            label="+"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col items-center justify-center gap-s5">
                                    <div className="text-center">
                                        <div className="text-sm leading-[19.6px] text-neutral-500">
                                            Avg. amount invested by each friend
                                        </div>
                                        <h5 className="text-3xl font-bold leading-[-1.4px] text-neutral-900">
                                            {formatIndianCurrency(amtValue)}
                                        </h5>
                                    </div>

                                    <CustomizableSlider
                                        value={[amtValue]}
                                        onValueChange={(value: number[]) => {
                                            setAmtValue(value[0]);
                                        }}
                                        defaultValue={[10000]}
                                        max={10000000}
                                        step={1}
                                        className="h-2 rounded-lg bg-gray-200"
                                        rangeClassName="bg-brand-primary-500"
                                        thumbClassName="border-grey-200"
                                    />
                                </div>
                            </div>
                            <div className="my-s8 flex flex-1 items-center justify-center sm:mr-s6">
                                <div className="flex h-full min-w-[85%] max-w-[85%] items-center justify-center rounded-xl bg-neutral-50 py-s6 md:min-w-[270px] md:px-s13 md:py-s9">
                                    <div className="flex flex-col items-center gap-s4 p-s4">
                                        <div className="relative flex items-center justify-center gap-s3">
                                            <div className="text-nowrap text-xs leading-[16.8px] text-neutral-500">
                                                YOUR EARNING
                                            </div>
                                            <div className="group relative">
                                                <Info
                                                    width="16"
                                                    height="16"
                                                    className="text-neutral-300"
                                                />
                                                <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-xs text-white group-hover:block">
                                                    Earn up to ₹10,000 for every new referral
                                                </div>
                                            </div>
                                        </div>

                                        <div className="text-5xl font-bold leading-[40px] tracking-[-2.4px] text-neutral-900">
                                            {formatIndianCurrency(calculateReturns())}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ICMWrapper>
            <div
                className={`flex w-full flex-col items-center justify-center gap-s9 px-s9 ${active ? "hidden md:flex" : ""}`}
            >
                <RegularButton
                    noIcon
                    showRightIcon={false}
                    size={isMobile ? RegularButtonSize.Medium : RegularButtonSize.Large}
                    variant={
                        footerText ? ButtonVariant.PrimaryDefault : ButtonVariant.SecondaryDefault
                    }
                    className="max-w-1/2 w-full sm:w-3/5 md:max-w-fit bg-white text-black/80 hover:bg-white"
                    label="Refer a Friend"
                    onClick={() => {
                        const eventName = active
                            ? "Refer_Homepage_Calculator"
                            : "US_Listing_Page_Refer_Friend_Fold 8";
                        setAnalyticsEvent(`${process.env.ICM_URL}/refer`, eventName);
                        cleverTapTrackEvent(eventName, {});
                        // initBranch()
                        //     .then(() => {
                        //         trackEvent(eventName, "", "");
                        //     })
                        //     .catch((error) => console.error("Error initializing Branch:", error));
                        setRedirectTo("/refer");
                        router.push("/login");
                    }}
                />
                <p
                    className="hidden text-center leading-[19.2px] text-neutral-500 md:block"
                    dangerouslySetInnerHTML={{
                        __html: `
                    Learn more about our${" "}
                    <a href="${process.env.ICM_URL}/terms-of-use/" class="underline">referral policy.</a>`,
                    }}

                    /* ${footerText ? footerText : ""} <br /> */
                />
            </div>
        </>
    );
};

export default ReferCalculator;
